import { useState } from 'react';
import type { ReactNode } from 'react';
import { STAFF_BRANDING_FIELDS } from '@yourstore/domain';
import { useAction, useApi, useRead } from '../../../context';
import { Confirm, Failure } from '../../../bits';
import { Chip, Panel, useToast } from '../../../kit';
import { useT } from '../../../../messages';
import type { T } from '../../../../messages';
import { Dialog } from '../Dialog';
import { AppPreview, wordCount } from './AppPreview';
import { LookEditor, colourOk } from './LookEditor';
import type { LookDraft } from './LookEditor';
import './look.css';

/**
 * **Their app's look** — the staff app's named set on the Setup tab (console
 * redesign B8), moved out of the page editor's "Staff app" tab.
 *
 * The card says what the look is now — a verdict chip, the sketch, the seven
 * choices in words — and **Change the look…** opens the controls beside a live
 * sketch that moves as they do, with the two background photographs uploaded
 * straight from the operator's computer.
 *
 * ## Saving writes the look and nothing else
 *
 * The old tab shared one Publish with the website, so a colour tweak
 * republished the guest site and spent one of its five kept versions. Save
 * here calls `PUT …/branding/app-look`, which writes the staff document alone;
 * the website's document, version and history do not move. It confirms first,
 * in words that say when devices change and that there is no undo but setting
 * it again — the look has no versions, and the card does not pretend it does.
 *
 * **Clear** is the way back to how the app ships, and asks too.
 */
export function AppLook({
  tenantId,
  name,
  onChanged,
}: {
  tenantId: string;
  name: string;
  onChanged: () => void;
}): ReactNode {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const save = useAction();
  const read = useRead(() => api.appLook(tenantId), [tenantId]);
  const [draft, setDraft] = useState<LookDraft | null>(null);
  const [confirming, setConfirming] = useState<'save' | 'clear' | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const stored = lookOf(read.value?.branding_staff ?? null);
  const own = Object.keys(stored).length > 0;
  const uploadsConfigured = read.value?.asset_uploads_configured ?? false;

  const open = (): void => {
    save.clear();
    setDone(null);
    setDraft({ ...stored });
  };

  const valid = draft !== null && colourOk(draft['accent']) && colourOk(draft['ground']);
  const unchanged = draft !== null && sameLook(draft, stored);

  const write = async (look: LookDraft | null): Promise<void> => {
    const ok = await save.run(async () => {
      await api.saveAppLook(tenantId, look);
    });
    if (ok) {
      const sentence = look === null ? t('look.cleared') : t('look.saved');
      setConfirming(null);
      setDraft(null);
      setDone(sentence);
      toast(sentence);
      read.reload();
      onChanged();
    }
  };

  return (
    <Panel
      title={t('look.title')}
      className="look"
      actions={
        read.value === null ? undefined : (
          <Chip tone={own ? 'accent' : undefined}>
            {own ? t('look.chip.own') : t('look.chip.standard')}
          </Chip>
        )
      }
    >
      {read.failure !== null ? <Failure failure={read.failure} /> : null}
      {read.value === null ? null : (
        <>
          <p className="faint">{t('look.lede', { name })}</p>
          <div className="look-card">
            <AppPreview name={name} values={stored} compact />
            {own ? (
              <Summary t={t} name={name} look={stored} />
            ) : (
              <p>{t('look.standard', { name })}</p>
            )}
          </div>
          {done !== null ? <p className="ok">{done}</p> : null}
          <div className="row-actions">
            <button type="button" className="button button--sm" onClick={open}>
              {t('look.change')}
            </button>
            {own ? (
              <button
                type="button"
                className="button button--quiet button--sm"
                onClick={() => {
                  save.clear();
                  setDone(null);
                  setConfirming('clear');
                }}
              >
                {t('look.clear')}
              </button>
            ) : null}
          </div>
        </>
      )}

      {draft !== null ? (
        <Dialog
          title={t('look.editTitle', { name })}
          size="large"
          busy={save.busy}
          onClose={() => {
            save.clear();
            setDraft(null);
          }}
          footer={
            <>
              <span className="faint">{t('look.draftNote')}</span>
              <button
                type="button"
                className="button button--primary"
                disabled={!valid || unchanged || save.busy}
                onClick={() => {
                  save.clear();
                  setConfirming('save');
                }}
              >
                {t('look.save')}
              </button>
            </>
          }
        >
          <div className="look-edit">
            <LookEditor
              tenantId={tenantId}
              name={name}
              draft={draft}
              uploadsConfigured={uploadsConfigured}
              onChange={setDraft}
            />
            <aside className="look-edit__preview">
              <h4>{t('look.preview.title')}</h4>
              <AppPreview name={name} values={draft} />
            </aside>
          </div>
        </Dialog>
      ) : null}

      {confirming === 'save' && draft !== null ? (
        <Confirm
          title={t('look.confirm.title', { name })}
          consequence={t('look.confirm.consequence', { name })}
          confirmLabel={t('look.save')}
          tone="primary"
          busy={save.busy}
          failure={save.failure}
          onConfirm={() => void write(draft)}
          onCancel={() => {
            save.clear();
            setConfirming(null);
          }}
        />
      ) : null}

      {confirming === 'clear' ? (
        <Confirm
          title={t('look.clearConfirm.title', { name })}
          consequence={t('look.clearConfirm.consequence', { name })}
          confirmLabel={t('look.clearConfirm.confirm')}
          busy={save.busy}
          failure={save.failure}
          onConfirm={() => void write(null)}
          onCancel={() => {
            save.clear();
            setConfirming(null);
          }}
        />
      ) : null}
    </Panel>
  );
}

/** The stored document as a draft: only the named fields, only non-empty strings. */
function lookOf(stored: Record<string, string> | null): LookDraft {
  const out: LookDraft = {};
  if (stored === null) return out;
  for (const field of STAFF_BRANDING_FIELDS) {
    const value = stored[field];
    if (typeof value === 'string' && value !== '') out[field] = value;
  }
  return out;
}

function sameLook(a: LookDraft, b: LookDraft): boolean {
  return STAFF_BRANDING_FIELDS.every((field) => (a[field] ?? '') === (b[field] ?? ''));
}

/** The seven choices in words, with the two colours as swatches. */
function Summary({ t, name, look }: { t: T; name: string; look: LookDraft }): ReactNode {
  const portrait = look['background_portrait_url'] !== undefined;
  const landscape = look['background_landscape_url'] !== undefined;
  const photos =
    portrait && landscape
      ? t('look.summary.photosBoth')
      : portrait
        ? t('look.summary.photosPortrait')
        : landscape
          ? t('look.summary.photosLandscape')
          : t('look.summary.photosNone');
  const standard = t('look.summary.standardValue');
  return (
    <dl className="kv look-summary">
      <dt>{t('look.summary.accent')}</dt>
      <dd>
        <Swatch value={look['accent']} fallback={standard} />
      </dd>
      <dt>{t('look.summary.ground')}</dt>
      <dd>
        <Swatch value={look['ground']} fallback={standard} />
      </dd>
      <dt>{t('look.summary.headings')}</dt>
      <dd>{look['font_display'] === 'serif' ? t('look.summary.serif') : standard}</dd>
      <dt>{t('look.summary.mark')}</dt>
      <dd>
        {look['brandmark'] === 'hearts'
          ? wordCount(name) < 3
            ? t('look.summary.heartsShort')
            : t('look.summary.hearts')
          : t('look.summary.plain')}
      </dd>
      <dt>{t('look.summary.button')}</dt>
      <dd>{look['action_icon'] === 'heart' ? t('look.summary.heart') : t('look.summary.plus')}</dd>
      <dt>{t('look.summary.photos')}</dt>
      <dd>{photos}</dd>
    </dl>
  );
}

function Swatch({ value, fallback }: { value: string | undefined; fallback: string }): ReactNode {
  if (value === undefined) return <>{fallback}</>;
  return (
    <span className="look-swatch">
      <span className="look-colour__swatch" style={{ background: value }} />
      <code>{value}</code>
    </span>
  );
}
